import { useState } from "react";
import { Trophy, User, Mail, Phone, ArrowRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PalpiteModal = ({ open, onOpenChange, onCheckout }) => {
  const [step, setStep] = useState(1); // 1 = dados, 2 = palpite
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    whatsapp: ""
  });
  const [loading, setLoading] = useState(false);

  // Reset quando fechar o modal
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setStep(1);
      setFormData({ nome: "", email: "", whatsapp: "" });
    }
    onOpenChange(isOpen);
  };

  // Formatar WhatsApp
  const formatWhatsApp = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleWhatsAppChange = (e) => {
    const formatted = formatWhatsApp(e.target.value);
    setFormData({ ...formData, whatsapp: formatted });
  };

  // Avançar para o palpite
  const handleContinue = (e) => {
    e.preventDefault();
    if (formData.nome && formData.whatsapp) {
      setStep(2);
    }
  };

  // Enviar palpite
  const handlePalpite = async (palpite) => {
    setLoading(true);
    
    try {
      // Enviar para o backend
      await fetch(`${API_URL}/api/clicks/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          palpite,
          nome: formData.nome,
          email: formData.email,
          whatsapp: formData.whatsapp
        })
      });
    } catch (error) {
      console.error('Erro ao registrar:', error);
    }
    
    setLoading(false);
    handleOpenChange(false);
    onCheckout(palpite);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-gradient-to-b from-[#002776] to-[#003399] border-2 border-[#FFDF00]">
        
        {/* STEP 1: Dados do usuário */}
        {step === 1 && (
          <div className="py-4">
            <div className="w-16 h-16 rounded-full bg-[#FFDF00] flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-[#002776]" />
            </div>
            
            <h2 className="font-heading text-2xl text-white mb-2 text-center">
              PARTICIPE DO DESAFIO! 🇧🇷
            </h2>
            <p className="text-white/80 text-sm mb-6 text-center">
              Preencha seus dados para fazer seu palpite
            </p>
            
            <form onSubmit={handleContinue} className="space-y-4">
              {/* Nome */}
              <div>
                <label className="text-white/80 text-sm mb-1 block">Nome *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <input
                    type="text"
                    required
                    placeholder="Seu nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FFDF00]"
                  />
                </div>
              </div>
              
              {/* Email */}
              <div>
                <label className="text-white/80 text-sm mb-1 block">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FFDF00]"
                  />
                </div>
              </div>
              
              {/* WhatsApp */}
              <div>
                <label className="text-white/80 text-sm mb-1 block">WhatsApp *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <input
                    type="tel"
                    required
                    placeholder="(00) 00000-0000"
                    value={formData.whatsapp}
                    onChange={handleWhatsAppChange}
                    className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FFDF00]"
                  />
                </div>
              </div>
              
              {/* Disclaimer */}
              <p className="text-white/50 text-xs text-center">
                🔒 Fique tranquilo! Você não receberá nenhum e-mail ou mensagem.
                <br />Seus dados são apenas para registro do palpite.
              </p>
              
              {/* Botão continuar */}
              <button
                type="submit"
                className="w-full bg-[#FFDF00] hover:bg-[#f5d400] text-[#002776] font-black py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] shadow-lg text-base flex items-center justify-center gap-2"
              >
                CONTINUAR PARA O PALPITE
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}
        
        {/* STEP 2: Escolher palpite */}
        {step === 2 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-[#FFDF00] flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-[#002776]" />
            </div>
            
            <h2 className="font-heading text-2xl text-white mb-2">
              FAÇA SEU PALPITE! 🇧🇷
            </h2>
            <p className="text-white/80 text-sm mb-2">
              Olá, <span className="text-[#FFDF00] font-bold">{formData.nome.split(' ')[0]}</span>!
            </p>
            <p className="text-white/80 text-sm mb-6">
              Brasil GANHA ou NÃO GANHA a Copa?
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => handlePalpite("brasil_ganha")}
                disabled={loading}
                className="w-full bg-[#009C3B] hover:bg-[#007830] border-2 border-[#FFDF00] rounded-xl p-4 text-white transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="palpite-ganha-btn"
              >
                <span className="text-2xl">🏆</span>
                <span className="font-bold">BRASIL VAI GANHAR!</span>
              </button>
              
              <button
                onClick={() => handlePalpite("brasil_perde")}
                disabled={loading}
                className="w-full bg-[#dc2626] hover:bg-[#b91c1c] border-2 border-[#FFDF00] rounded-xl p-4 text-white transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="palpite-perde-btn"
              >
                <span className="text-2xl">😅</span>
                <span className="font-bold">BRASIL NÃO VAI GANHAR</span>
              </button>
            </div>
            
            <p className="text-white/60 text-xs mt-4">
              Acertou = dinheiro de volta OU dobro de ingressos!
            </p>
            
            {/* Voltar */}
            <button
              onClick={() => setStep(1)}
              className="text-white/50 text-sm mt-4 hover:text-white/80 transition-colors"
            >
              ← Voltar e editar dados
            </button>
          </div>
        )}
        
      </DialogContent>
    </Dialog>
  );
};

export default PalpiteModal;
